/**
 * The parity screen — PD-5.
 *
 * The scenario-run view answers "did THIS run diverge"; this answers the
 * question the cutover actually turns on: across everything measured in a
 * window, which field families agree well enough to read from the reasoner's
 * `extracted` instead of from `parse(story_text)`.
 *
 * It is deep-linkable by pebble id (`/parity/:pebbleId`) for the same reason
 * the timeline and the match-events log are: every other screen that shows a
 * pebble should be able to hand it over.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getExtractedParity } from '../api/client';
import ParityReport from '../components/comparison/ParityReport';
import { PARITY_FAMILIES } from '../components/comparison/parity-model';
import type { ExtractedParityReport } from '../api/types';

const WINDOWS = [
  { label: '24 h', days: 1 },
  { label: '7 d', days: 7 },
  { label: '30 d', days: 30 },
  { label: 'all', days: 0 },
];

export default function ParityPage() {
  const { pebbleId } = useParams<{ pebbleId: string }>();
  const navigate = useNavigate();
  const [pebbleInput, setPebbleInput] = useState(pebbleId ?? '');
  const [family, setFamily] = useState('');
  const [windowDays, setWindowDays] = useState(7);
  const [rows, setRows] = useState(200);
  const [report, setReport] = useState<ExtractedParityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => setPebbleInput(pebbleId ?? ''), [pebbleId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getExtractedParity({
      pebbleId: pebbleId || undefined,
      family: family || undefined,
      windowDays: windowDays > 0 ? windowDays : undefined,
      rows,
    })
      .then((res) => !cancelled && setReport(res))
      .catch((e) => {
        if (cancelled) return;
        setReport(null);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [pebbleId, family, windowDays, rows]);

  const scope = [
    pebbleId ? `Pebble ${pebbleId}.` : null,
    windowDays > 0 ? `Last ${windowDays} d.` : 'All time.',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">extracted_v1 parity</h1>
        <p className="text-sm text-gray-500">
          §10.3 “parity before trust” — the matcher computes both paths on every reasoner-sourced
          submission and keeps score. A family reads from <span className="font-mono">extracted</span>{' '}
          only once its measured agreement clears the gate.
        </p>
      </div>

      <div className="card flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs text-gray-600">Pebble</span>
          <input
            className="mt-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono"
            placeholder="all pebbles"
            value={pebbleInput}
            onChange={(e) => setPebbleInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(pebbleInput.trim() ? `/parity/${pebbleInput.trim()}` : '/parity');
              }
            }}
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-600">Family</span>
          <select
            className="mt-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            aria-label="family"
          >
            <option value="">all families</option>
            {PARITY_FAMILIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-600">Window</span>
          <select
            className="mt-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            aria-label="window"
          >
            {WINDOWS.map((w) => (
              <option key={w.label} value={w.days}>
                {w.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-600">Rows</span>
          <select
            className="mt-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            value={rows}
            onChange={(e) => setRows(Number(e.target.value))}
            aria-label="rows"
          >
            {[0, 50, 200, 1000].map((n) => (
              <option key={n} value={n}>
                {n === 0 ? 'summary only' : n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ParityReport report={report} unavailable={error} loading={loading} scope={scope} />
    </div>
  );
}
